import { DOMRectangle } from "@dnd-kit/dom/utilities";
import type { Position } from "@dnd-kit/geometry";
import {
  DragDropProvider,
  DragOverlay,
  useDragDropMonitor,
} from "@dnd-kit/react";
import {
  getBaseRect,
  getPiecesCSSScale,
  posRelFrom,
  posRelTo,
  useMovableToRect,
  useSnapPos,
} from "components/Movables/pos";
import { useOnDragEnd } from "components/Movables/util";
import { AnimatePresence, useAnimate } from "framer-motion";
import { createContext, ReactNode, useContext } from "react";
import { useTransformContext } from "react-zoom-pan-pinch";
import { PuzzleMetadata, RoundData } from "sockapi/puzzle-data";
import {
  useGetRoundStateNonReactive,
  useSelectMovable,
} from "stores/RoundStore";
import "./MovableProvider.css";

type MovableContext =
  | { type: "round"; round: RoundData | null }
  | {
      type: "puzzle";
      round: RoundData;
      puzzle: PuzzleMetadata & Required<Pick<PuzzleMetadata, "cols" | "rows">>;
    }
  | null;

const MovableContext = createContext<MovableContext>(null);

export const useMovableContext = () => {
  const type = useContext(MovableContext);
  return type!;
};

export const useTransformContextSafe = () => {
  try {
    return useTransformContext();
  } catch {
    return null;
  }
};

function setFocusedElement(elt?: Element) {
  for (const other of document.getElementsByClassName("drag-focused"))
    other.classList.remove("drag-focused");
  elt?.classList.add("drag-focused");
}

function DragLayer() {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const getRoundStateNonReactive = useGetRoundStateNonReactive();
  const onDragEnd = useOnDragEnd();
  const select = useSelectMovable();
  const snapPos = useSnapPos();
  const toRect = useMovableToRect();
  const transformCtx = useTransformContextSafe();

  const positionToDPos = (position: Position, baseElemScale: number) => {
    const panZoomScale = transformCtx?.transformState.scale ?? 1;
    return {
      x:
        (position.current.x - position.initial.x) /
        panZoomScale /
        baseElemScale,
      y:
        (position.current.y - position.initial.y) /
        panZoomScale /
        baseElemScale,
    };
  };

  useDragDropMonitor({
    onBeforeDragStart(event) {
      if ((event.operation.activatorEvent as MouseEvent)?.shiftKey) {
        // Use the round-level panning.
        event.preventDefault();
      }
    },
    onDragStart(event) {
      const source = event.operation.source;
      if (source === null) return;
      setFocusedElement(source.element);
      if (
        source.data.type === "piece" ||
        source.data.type === "boxPiece" ||
        source.data.type === "box"
      ) {
        select(source.id as string);
      }
    },
    onDragMove(event, manager) {
      const { source, position } = event.operation;
      const id = source?.id as string | null;
      const base = getBaseRect();
      const scale = getPiecesCSSScale();
      if (
        !id ||
        getRoundStateNonReactive === null ||
        !scope.current ||
        base === null ||
        scale === null
      )
        return;
      const dPos = positionToDPos(position, scale);
      const { derived } = getRoundStateNonReactive();
      const rect = toRect(derived.movables[id]!, base);
      const rectNew = posRelTo(snapPos(posRelFrom(rect, dPos), base), base);
      animate(scope.current, rectNew, { duration: 0 });
      manager.dragOperation.shape = new DOMRectangle(scope.current);
    },
    onDragEnd(event) {
      const { operation, canceled } = event;
      setFocusedElement();
      if (canceled) return;
      const { source, target, position } = operation;
      const scale = getPiecesCSSScale();
      if (target === null || source === null || scale === null) return;
      const dPos = positionToDPos(position, scale);
      onDragEnd({
        source: source.id as string,
        target: target.id as string,
        dPos,
      });
    },
  });

  return (
    <div className="drag-layer">
      <DragOverlay
        style={{ display: "block", visibility: "visible", height: "100%" }}
      >
        <div className="drag-preview" ref={scope} />
      </DragOverlay>
    </div>
  );
}

export default function MovableProvider({
  children,
  context,
}: {
  children: ReactNode;
  context: MovableContext;
}) {
  return (
    <AnimatePresence>
      <MovableContext value={context}>
        <DragDropProvider>
          {children}
          <DragLayer />
        </DragDropProvider>
      </MovableContext>
    </AnimatePresence>
  );
}
